import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { Ticket } from '../../services/Ticket';
import AzureDevOpsService from '../../services/AzureDevOpsService';

interface InheritedFieldsProps {
  selectedTicket: Ticket;
  azureDevOpsService: AzureDevOpsService;
  fieldType: 'govProject' | 'projectNumber' | 'capex' | 'priorityDriver' | 'product' | 'investmentClass';
}

interface InheritedFieldsData {
  govProject: { value: string; inherited: boolean };
  projectNumber: { value: string; inherited: boolean };
  capex: { value: string; inherited: boolean };
  priorityDriver: { value: string; inherited: boolean };
  product: { value: string; inherited: boolean };
  investmentClass: { value: string; inherited: boolean };
}

const InheritedFields: React.FC<InheritedFieldsProps> = ({ selectedTicket, azureDevOpsService, fieldType }) => {
  const [inheritedFields, setInheritedFields] = useState<InheritedFieldsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInheritedFields = async () => {
      setLoading(true);
      setError(null);

      try {
        const workItemCache = new Map<string, any>();
        
        const currentWorkItem = await azureDevOpsService.fetchWorkItemById(selectedTicket.id);
        if (!currentWorkItem) {
          throw new Error('Failed to fetch current work item');
        }

        const inherited = await azureDevOpsService.getInheritedFields(currentWorkItem, workItemCache);
        setInheritedFields(inherited);

      } catch (err) {
        console.error('Error fetching inherited fields:', err);
        setError('Failed to load inherited field information');
      } finally {
        setLoading(false);
      }
    };

    fetchInheritedFields();
  }, [selectedTicket.id, azureDevOpsService]);

  if (loading) {
    return (
      <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
        <Spin size="small" /> Loading inherited value...
      </span>
    );
  }

  if (error) {
    return (
      <span style={{ fontSize: '12px', color: '#f5222d', marginLeft: '8px', fontStyle: 'italic' }}>
        (Error loading inherited value)
      </span>
    );
  }

  if (!inheritedFields) {
    return null;
  }

  const fieldData = inheritedFields[fieldType];
  
  if (!fieldData.inherited || !fieldData.value) {
    return null; // Don't show anything if not inherited or no value
  }


  return (
    <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', marginLeft: '8px' }}>
      {fieldData.value} (inherited)
    </span>
  );
};

export default InheritedFields;
